import { useState, useRef, useMemo } from 'react';
import { X, Archive, Download, StopCircle, FolderTree, ListFilter } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { normalizeUcCodigo, expandirChavesUc } from '../lib/ucCodigo';
import { propsFundoModal } from '../lib/backdropModal';
import {
  MES_NUMERO,
  ORIGEM_DISTRIBUIDORA,
  ORIGEM_VIGOR,
  caminhoNoZip,
  limparNome,
  type OrigemFatura,
} from '../lib/nomeArquivoFatura';

interface FaturaParaZip {
  id: number;
  uc_codigo: string;
  mes_referencia: string;
  tipo_pdf_fatura: string | null;
  pdf_fatura: string | null;
  url_boleto?: string | null;
  url_boleto_combinado?: string | null;
  empresa_parceira_id?: string | null;
  end_cidade?: string | null;
}

interface UnidadeParaAlias {
  numero_uc?: string | null;
  outro_numero_uc?: string | null;
}

interface ExportZipModalProps {
  isOpen: boolean;
  onClose: () => void;
  faturas: FaturaParaZip[];
  /**
   * Cadastro das UCs, usado só para o filtro por lista. A mesma unidade tem dois
   * números e `faturas.uc_codigo` ora traz um, ora outro — sem isso, colar a
   * lista que o pessoal usa no dia a dia acha uma fração das faturas.
   */
  unidades?: UnidadeParaAlias[];
  /**
   * `empresa_parceira_id` → nome, para rotular a subpasta do boleto com quem de
   * fato emitiu. Sem o mapa a parceira cai no rótulo genérico.
   */
  nomesParceiras?: Map<string, string>;
}

/**
 * O que entra no ZIP. Fora os três fixos, cada empresa parceira com fatura na
 * lista vira um `boletos:<id>` próprio: o antigo "Boletos da Vigor" era uma
 * opção só e levava junto o boleto da parceira sem dizer que era dela.
 */
type TipoArquivo = 'espelhadas' | 'nao_espelhadas' | 'boletos_vigor' | `boletos:${string}`;

const PREFIXO_BOLETO_PARCEIRA = 'boletos:';

/** Seleção inicial: as faturas da distribuidora, espelhadas ou não — o antigo "Todas". */
const TIPOS_PADRAO: TipoArquivo[] = ['espelhadas', 'nao_espelhadas'];

const RE_SEPARADOR = /[\n,;\t]+/;

/** Default içado: `[]` inline recriaria o array e invalidaria o useMemo a cada render. */
const SEM_UNIDADES: UnidadeParaAlias[] = [];
/** Mesmo motivo do `SEM_UNIDADES`. */
const SEM_PARCEIRAS = new Map<string, string>();

/**
 * O PDF do boleto e quem o emitiu.
 *
 * `url_boleto` guarda coisas diferentes conforme o gateway: na fatura da Vigor é
 * o PDF já hospedado no site, mas na fatura de empresa parceira é a **página da
 * cobrança no Asaas** — HTML, não boleto, e sem `Access-Control-Allow-Origin`,
 * então o `fetch` daqui morre em "Failed to fetch". Em AGO/2026 foram as 64
 * linhas do `_erros.txt`. O PDF de verdade da parceira está em
 * `url_boleto_combinado`, publicado no site pela `gerar-boleto-gran`.
 *
 * Mesma ordem de preferência de src/lib/linkBoleto.ts, com uma diferença: aqui
 * não existe fallback para a página do gateway. Parceira sem PDF publicado conta
 * como par incompleto em vez de virar uma linha de erro garantida.
 */
function boletoDaFatura(
  f: FaturaParaZip,
  nomesParceiras: Map<string, string>
): { url: string; origem: OrigemFatura } | null {
  if (!f.empresa_parceira_id) {
    return f.url_boleto ? { url: f.url_boleto, origem: ORIGEM_VIGOR } : null;
  }
  if (!f.url_boleto_combinado) return null;
  return {
    url: f.url_boleto_combinado,
    origem: nomesParceiras.get(f.empresa_parceira_id) || 'Parceira',
  };
}

/** Aceita a lista de UCs colada de qualquer jeito: linha, vírgula, ponto e vírgula ou tab. */
const parseListaUcs = (texto: string) =>
  new Set(texto.split(RE_SEPARADOR).map(t => normalizeUcCodigo(t.trim())).filter(Boolean));

export function ExportZipModal({
  isOpen,
  onClose,
  faturas,
  unidades = SEM_UNIDADES,
  nomesParceiras = SEM_PARCEIRAS,
}: ExportZipModalProps) {
  const mesesDisponiveis = useMemo(() => Array.from(
    new Set(faturas.map(f => f.mes_referencia).filter(Boolean))
  ).sort((a, b) => {
    const [mA, yA] = a.split('/');
    const [mB, yB] = b.split('/');
    if (yA !== yB) return Number(yB) - Number(yA);
    return (MES_NUMERO[mB] ?? 0) - (MES_NUMERO[mA] ?? 0);
  }), [faturas]);

  const [mesesSelecionados, setMesesSelecionados] = useState<string[]>(
    () => (mesesDisponiveis[0] ? [mesesDisponiveis[0]] : [])
  );
  const [tiposSelecionados, setTiposSelecionados] = useState<TipoArquivo[]>(TIPOS_PADRAO);
  const [porPasta, setPorPasta] = useState(false);
  const [filtroUcsTexto, setFiltroUcsTexto] = useState('');
  const [mostrarFiltroUcs, setMostrarFiltroUcs] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [progresso, setProgresso] = useState({ atual: 0, total: 0 });
  const [erros, setErros] = useState<string[]>([]);
  const [resumo, setResumo] = useState<{ arquivos: number; faltando: number } | null>(null);
  const cancelledRef = useRef(false);

  // Set, não array: este filtro roda contra a lista inteira de faturas (milhares).
  const ucsDigitadas = useMemo(() => parseListaUcs(filtroUcsTexto), [filtroUcsTexto]);
  // Inclui o outro número de cada unidade — ver expandirChavesUc.
  const ucsFiltro = useMemo(
    () => expandirChavesUc(ucsDigitadas, unidades),
    [ucsDigitadas, unidades]
  );
  const mesesFiltro = useMemo(() => new Set(mesesSelecionados), [mesesSelecionados]);
  const tiposFiltro = useMemo(() => new Set<string>(tiposSelecionados), [tiposSelecionados]);

  /**
   * Os chips do "Tipo de Fatura". As parceiras saem da própria lista de faturas
   * — hoje só a Gran, mas quem entrar depois aparece sozinha, sem mexer aqui.
   */
  const opcoesTipo = useMemo(() => {
    const idsParceiras = new Set<string>();
    for (const f of faturas) if (f.empresa_parceira_id) idsParceiras.add(f.empresa_parceira_id);
    const parceiras = Array.from(idsParceiras)
      .map(id => ({
        valor: `${PREFIXO_BOLETO_PARCEIRA}${id}` as TipoArquivo,
        label: `Boletos ${nomesParceiras.get(id) || 'Parceira'}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    return [
      { valor: 'espelhadas' as TipoArquivo, label: 'Espelhadas' },
      { valor: 'nao_espelhadas' as TipoArquivo, label: 'Não-Espelhadas' },
      { valor: 'boletos_vigor' as TipoArquivo, label: 'Boletos Vigor' },
      ...parceiras,
    ];
  }, [faturas, nomesParceiras]);

  /** Faturas dentro dos meses e das UCs escolhidos, antes de olhar quais têm PDF. */
  const faturasNoRecorte = useMemo(() => faturas.filter(f => {
    if (!mesesFiltro.has(f.mes_referencia)) return false;
    if (ucsFiltro.size > 0 && !ucsFiltro.has(normalizeUcCodigo(f.uc_codigo))) return false;
    return true;
  }), [faturas, mesesFiltro, ucsFiltro]);

  /**
   * O que efetivamente entra no ZIP. No modo por pasta cada fatura pode render
   * dois arquivos (a da distribuidora e o boleto), por isso a lista é de itens e
   * não de faturas.
   */
  const { itens, paresIncompletos } = useMemo(() => {
    const lista: { fatura: FaturaParaZip; url: string; origem: OrigemFatura }[] = [];
    let incompletos = 0;
    for (const f of faturasNoRecorte) {
      const boleto = boletoDaFatura(f, nomesParceiras);
      if (porPasta) {
        if (f.pdf_fatura) lista.push({ fatura: f, url: f.pdf_fatura, origem: ORIGEM_DISTRIBUIDORA });
        if (boleto) lista.push({ fatura: f, ...boleto });
        if (!f.pdf_fatura || !boleto) incompletos++;
        continue;
      }
      // Fatura da distribuidora e boleto são tipos independentes: marcando os
      // dois, a mesma fatura rende os dois arquivos.
      if (f.pdf_fatura) {
        const tipoPdf = f.tipo_pdf_fatura === 'espelho' ? 'espelhadas' : 'nao_espelhadas';
        if (tiposFiltro.has(tipoPdf)) {
          lista.push({ fatura: f, url: f.pdf_fatura, origem: ORIGEM_DISTRIBUIDORA });
        }
      }
      if (boleto) {
        const tipoBoleto = f.empresa_parceira_id
          ? `${PREFIXO_BOLETO_PARCEIRA}${f.empresa_parceira_id}`
          : 'boletos_vigor';
        if (tiposFiltro.has(tipoBoleto)) lista.push({ fatura: f, ...boleto });
      }
    }
    return { itens: lista, paresIncompletos: incompletos };
  }, [faturasNoRecorte, porPasta, tiposFiltro, nomesParceiras]);

  if (!isOpen) return null;

  const toggleMes = (mes: string) => {
    setMesesSelecionados(prev =>
      prev.includes(mes) ? prev.filter(m => m !== mes) : [...prev, mes]
    );
  };

  const toggleTipo = (tipo: TipoArquivo) => {
    setTiposSelecionados(prev =>
      prev.includes(tipo) ? prev.filter(t => t !== tipo) : [...prev, tipo]
    );
  };

  /** Pedaço do nome do ZIP. A parceira entra pelo nome, não pelo id. */
  const slugDoTipo = (tipo: TipoArquivo) => {
    if (!tipo.startsWith(PREFIXO_BOLETO_PARCEIRA)) return tipo.replace('_', '-');
    const nome = nomesParceiras.get(tipo.slice(PREFIXO_BOLETO_PARCEIRA.length)) || 'parceira';
    return `boletos-${limparNome(nome).toLowerCase().replace(/\s+/g, '-')}`;
  };

  async function handleExportar() {
    if (itens.length === 0) {
      setErros(['Nenhum arquivo encontrado para os filtros selecionados.']);
      setStatus('error');
      return;
    }

    cancelledRef.current = false;
    setStatus('loading');
    setErros([]);
    setResumo(null);
    setProgresso({ atual: 0, total: itens.length });

    const zip = new JSZip();
    const novosErros: string[] = [];
    let baixados = 0;

    for (let i = 0; i < itens.length; i++) {
      if (cancelledRef.current) {
        setStatus('idle');
        setProgresso({ atual: 0, total: 0 });
        return;
      }

      const { fatura, url, origem } = itens[i];
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();

        if (porPasta) {
          // `caminhoNoZip` em vez de encadear `folder()`: a origem agora é o nome
          // da parceira, texto de cadastro, e é lá que ele é higienizado.
          zip.file(caminhoNoZip(fatura, origem), blob);
        } else {
          const segmentos = url.split('/');
          const nome = segmentos[segmentos.length - 1].split('?')[0]
            || `${origem.toLowerCase()}_${fatura.uc_codigo}_${fatura.mes_referencia.replace('/', '_')}.pdf`;
          zip.file(nome, blob);
        }
        baixados++;
      } catch (e: any) {
        novosErros.push(`UC ${fatura.uc_codigo} · ${fatura.mes_referencia} · ${origem}: ${e.message}`);
      }
      setProgresso({ atual: i + 1, total: itens.length });
    }

    const sufixoMes = mesesSelecionados.length === 1
      ? mesesSelecionados[0].replace('/', '_')
      : `${mesesSelecionados.length}-meses`;
    const tipoLabel = porPasta ? 'por-unidade'
      : tiposSelecionados.length === opcoesTipo.length ? 'todas'
        : tiposSelecionados.length > 2 ? `${tiposSelecionados.length}-tipos`
          : tiposSelecionados.map(slugDoTipo).join('_');

    // O que não baixou vai junto no ZIP: sem isso o operador só descobre a falha
    // conferindo pasta por pasta.
    if (novosErros.length > 0) zip.file('_erros.txt', novosErros.join('\n'));

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `faturas_${sufixoMes}_${tipoLabel}.zip`);

    setErros(novosErros);
    setResumo({ arquivos: baixados, faltando: paresIncompletos });
    setStatus('done');
  }

  function handleClose() {
    setStatus('idle');
    setErros([]);
    setResumo(null);
    setProgresso({ atual: 0, total: 0 });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      {...propsFundoModal(() => {
        if (status === 'loading') { cancelledRef.current = true; } else { handleClose(); }
      })}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col border border-gray-100 dark:border-gray-700" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
              <Archive size={20} />
            </div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Exportar Faturas ZIP</h2>
          </div>
          <button
            onClick={status === 'loading' ? () => { cancelledRef.current = true; } : handleClose}
            className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Meses de Referência
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMesesSelecionados(mesesDisponiveis.slice(0, 6))}
                  disabled={status === 'loading'}
                  className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
                >
                  Últimos 6
                </button>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <button
                  onClick={() => setMesesSelecionados([])}
                  disabled={status === 'loading'}
                  className="text-[11px] font-semibold text-gray-400 hover:text-red-500 disabled:opacity-50"
                >
                  Limpar
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto custom-scrollbar p-0.5">
              {mesesDisponiveis.map(mes => (
                <button
                  key={mes}
                  onClick={() => toggleMes(mes)}
                  disabled={status === 'loading'}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-colors disabled:opacity-50 ${mesesSelecionados.includes(mes)
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary/50'
                    }`}
                >
                  {mes}
                </button>
              ))}
            </div>
          </div>

          {/* Organização em pastas */}
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${porPasta
            ? 'border-primary bg-primary/5 dark:bg-primary/10'
            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}>
            <input
              type="checkbox"
              checked={porPasta}
              onChange={e => setPorPasta(e.target.checked)}
              disabled={status === 'loading'}
              className="mt-0.5 accent-primary"
            />
            <div className="min-w-0">
              <p className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                <FolderTree size={13} className="text-primary" />
                Organizar em pastas por unidade
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Uma pasta por UC, com a subpasta <strong>Equatorial</strong> e a de quem emitiu o boleto
                (<strong>Vigor</strong>, ou o nome da parceira), e o arquivo já renomeado no padrão{' '}
                <code className="text-[10px] bg-gray-100 dark:bg-gray-700 px-1 rounded">nº-UC-Cidade-Origem.pdf</code>.
              </p>
            </div>
          </label>

          {porPasta ? null : (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo de Fatura
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTiposSelecionados(opcoesTipo.map(o => o.valor))}
                    disabled={status === 'loading'}
                    className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
                  >
                    Todos
                  </button>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <button
                    onClick={() => setTiposSelecionados([])}
                    disabled={status === 'loading'}
                    className="text-[11px] font-semibold text-gray-400 hover:text-red-500 disabled:opacity-50"
                  >
                    Limpar
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 p-0.5">
                {opcoesTipo.map(opcao => (
                  <button
                    key={opcao.valor}
                    onClick={() => toggleTipo(opcao.valor)}
                    disabled={status === 'loading'}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-colors disabled:opacity-50 ${tiposSelecionados.includes(opcao.valor)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary/50'
                      }`}
                  >
                    {opcao.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filtro por lista de UCs */}
          <div>
            <button
              onClick={() => setMostrarFiltroUcs(v => !v)}
              disabled={status === 'loading'}
              className="flex items-center gap-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-primary transition-colors disabled:opacity-50"
            >
              <ListFilter size={13} />
              Filtrar por lista de UCs
              {ucsDigitadas.size > 0 ? (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary text-white text-[10px] normal-case tracking-normal">
                  {ucsDigitadas.size}
                </span>
              ) : null}
            </button>
            {mostrarFiltroUcs ? (
              <textarea
                value={filtroUcsTexto}
                onChange={e => setFiltroUcsTexto(e.target.value)}
                disabled={status === 'loading'}
                rows={4}
                placeholder={'Cole as UCs — uma por linha, ou separadas por vírgula.\n10029809353\n760000116\n430004424'}
                className="mt-2 w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            ) : null}
          </div>

          {/* Status */}
          {status === 'idle' ? (
            <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3 space-y-1">
              {mesesSelecionados.length === 0 ? (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  Selecione ao menos um mês.
                </p>
              ) : !porPasta && tiposSelecionados.length === 0 ? (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  Selecione ao menos um tipo de fatura.
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    <strong>{itens.length}</strong> arquivo{itens.length !== 1 ? 's' : ''} em{' '}
                    <strong>{mesesSelecionados.length}</strong> {mesesSelecionados.length !== 1 ? 'meses' : 'mês'}
                    {ucsDigitadas.size > 0 ? <> · <strong>{ucsDigitadas.size}</strong> UC{ucsDigitadas.size !== 1 ? 's' : ''} no filtro</> : null}
                  </p>
                  {porPasta && paresIncompletos > 0 ? (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      {paresIncompletos} UC/mês do recorte não tem o par completo — o que faltar precisa
                      ser baixado no portal da distribuidora.
                    </p>
                  ) : null}
                  {ucsDigitadas.size > 0 && faturasNoRecorte.length === 0 ? (
                    <p className="text-[11px] text-red-600 dark:text-red-400">
                      Nenhuma fatura casou com essas UCs nos meses escolhidos.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {status === 'loading' ? (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Baixando arquivos...</span>
                <span>{progresso.atual}/{progresso.total}</span>
              </div>
              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-green-500 h-1.5 rounded-full transition-all duration-200"
                  style={{ width: `${progresso.total > 0 ? (progresso.atual / progresso.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          ) : null}

          {status === 'done' && resumo ? (
            <div className="rounded-xl bg-green-50 dark:bg-green-900/20 p-3 space-y-1">
              <p className="text-xs text-green-700 dark:text-green-400 font-bold">
                ZIP gerado — {resumo.arquivos} arquivo{resumo.arquivos !== 1 ? 's' : ''}.
              </p>
              {erros.length > 0 ? (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  {erros.length} não baixou. A lista foi para o <strong>_erros.txt</strong> dentro do ZIP.
                </p>
              ) : null}
              {resumo.faltando > 0 ? (
                <p className="text-[11px] text-gray-600 dark:text-gray-400">
                  {resumo.faltando} UC/mês ficou incompleto no recorte.
                </p>
              ) : null}
            </div>
          ) : null}

          {status === 'error' && erros.length > 0 ? (
            <p className="text-xs text-red-600 dark:text-red-400">{erros[0]}</p>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-6 pt-4 border-t border-gray-100 dark:border-gray-700 shrink-0">
          {status === 'loading' ? (
            <button
              onClick={() => { cancelledRef.current = true; }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <StopCircle size={15} />
              Cancelar
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Fechar
            </button>
          )}
          <button
            onClick={handleExportar}
            disabled={status === 'loading' || itens.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-700/20"
          >
            <Download size={15} />
            {status === 'loading' ? 'Exportando...' : 'Exportar ZIP'}
          </button>
        </div>
      </div>
    </div>
  );
}
